import type {
  FlavorFinderAnswerDetails,
  FlavorFinderAnswerRaw,
  FlavorFinderOptionalQuestionTransitions,
  FlavorFinderQuestionRaw,
  FlavorFinderQuestions,
  FlavorFinderQuizResults,
} from "@jti/ui/components/FlavorFinder/types/FlavorFinderTypes";

interface AnswerData extends FlavorFinderAnswerDetails {
  relation?: {
    id: string;
  };
  resultRelation?: {
    id: string;
  };
  nextQuestionOrResults?: {
    id: string;
  };
}

export interface QuizModelType {
  id: string;
  data: {
    id?: string;
    answers: AnswerData[];
    title: string;
    referenceName: string;
    alternativeText?: string;
    localTranslation?: string;
    relatedQuestion?: {
      id: string;
    };
    quizId?: string;
  };
}

export interface OptionalQuizModelType {
  id: string;
  data: {
    id?: string;
    title: string;
    referenceName: string;
    indicatorOverrideLabel: string;
    localTranslation?: string;
    possibleAnswers: AnswerData[];
    alternativeText?: string;
    desktopBackgroundImage?: string;
    tabletBackgroundImage?: string;
    mobileBackgroundImage?: string;
    relatedQuestion?: {
      id: string;
    };
  };
}

export interface ResultModelType {
  data: FlavorFinderQuizResults;
  id: string;
}

export type ResultsModelType = {
  id: string;
  data: {
    results: [
      {
        data: {
          id: string;
        };
      },
    ];
  };
};

export class QuizModel {
  private quizModel?: QuizModelType[];
  private resultsModel?: ResultsModelType[];
  private resultModel?: ResultModelType[];
  private SKU_QUERY_PARAM_FORMAT = "sku_%s_quantity=1";

  quizTransitions: FlavorFinderQuestions[] = [];
  questionDimensions: FlavorFinderQuestionRaw[] = [];
  answerDimensions: FlavorFinderAnswerRaw[] = [];
  finalQuestion?: FlavorFinderOptionalQuestionTransitions;

  constructor(
    quizModel: QuizModelType[],
    resultsModel: ResultsModelType[],
    resultModel: ResultModelType[],
    optionalQuizModel: OptionalQuizModelType | undefined,
    resultsLink: string,
    resultsLinkWithDevice: string
  ) {
    this.quizModel = quizModel;
    this.resultsModel = resultsModel;
    this.resultModel = resultModel;

    this.parseQuizzes();
    this.parseOptionalQuiz(optionalQuizModel);
    this.bindOnAnswerIds(resultsLink, resultsLinkWithDevice);
  }

  private darkenHexColor = (hexColor: string, percent: number): string => {
    const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{6})$/;

    if (!HEX_COLOR_PATTERN.test(hexColor)) {
      return hexColor;
    }

    percent = Math.max(0, Math.min(100, percent));

    let r = parseInt(hexColor.substring(1, 3), 16);
    let g = parseInt(hexColor.substring(3, 5), 16);
    let b = parseInt(hexColor.substring(5, 7), 16);

    const adjust = (percent / 100) * 255;

    r = Math.round(r - adjust);
    g = Math.round(g - adjust);
    b = Math.round(b - adjust);

    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    const asHexString = (value: number) => value.toString(16).padStart(2, "0");

    return `#${asHexString(r)}${asHexString(g)}${asHexString(b)}`;
  };

  private dynamicallyAssociate(resultsLink: string, results: FlavorFinderQuizResults[] | undefined): string {
    const queryParameters = results?.map((result) => this.SKU_QUERY_PARAM_FORMAT.replace("%s", result.sku)).join("&");

    return `${resultsLink}?${queryParameters}`;
  }

  private parseQuizzes() {
    this.quizModel?.forEach((question) => {
      const possibleAnswers = question.data.answers.map((answer, index) => ({
        title: answer.title,
        referenceName: answer.referenceName ?? "",
        backgroundImage: answer.backgroundImage,
        tabletBackgroundImage: answer.tabletBackgroundImage,
        mobileBackgroundImage: answer.mobileBackgroundImage,
        localTranslation: answer.localTranslation,
        id: `${question.id}_${index}`,
      }));

      const questionDimension: FlavorFinderQuestionRaw = {
        question_id: question?.id ?? "",
        reference_name: question?.data.referenceName,
        local_translation: question?.data.localTranslation ?? "",
        type: "FLAVOR_FINDER",
      };
      this.questionDimensions.push(questionDimension);

      const answersDimension = possibleAnswers?.flatMap((answer) => ({
        question_id: question.id,
        answer_id: answer.id,
        reference_name: answer.referenceName,
        local_translation: answer.localTranslation ?? "",
        type: "FLAVOR_FINDER",
      }));
      this.answerDimensions.push(...answersDimension);

      const quizTransition: FlavorFinderQuestions = {
        question: {
          id: question.id,
          title: question.data.title,
          indicatorOverrideLabel: question.data.alternativeText,
        },
        possibleAnswers,
        quizId: question?.data.quizId,
      };
      this.quizTransitions.push(quizTransition);
    });
  }

  private parseOptionalQuiz(optionalQuizModel: OptionalQuizModelType | undefined) {
    if (!optionalQuizModel) {
      return;
    }

    const optionalQuestionDimension: FlavorFinderQuestionRaw = {
      question_id: optionalQuizModel.id ?? "",
      reference_name: optionalQuizModel.data.referenceName ?? "",
      type: "FLAVOR_FINDER",
      local_translation: optionalQuizModel.data.localTranslation ?? "",
    };

    const answersDimension = optionalQuizModel.data.possibleAnswers.flatMap((answer, index) => ({
      question_id: optionalQuizModel.id ?? "",
      answer_id: `${optionalQuizModel.id ?? ""}_${index}`,
      reference_name: answer.referenceName ?? "",
      local_translation: answer.localTranslation ?? "",
      type: "FLAVOR_FINDER",
    }));

    this.finalQuestion = {
      question: {
        id: optionalQuizModel.id,
        title: optionalQuizModel.data.title,
        image: optionalQuizModel.data.desktopBackgroundImage,
        mediumImage: optionalQuizModel.data.tabletBackgroundImage,
        smallImage: optionalQuizModel.data.mobileBackgroundImage,
        indicatorOverrideLabel: optionalQuizModel.data.indicatorOverrideLabel,
      },
      possibleAnswers: optionalQuizModel.data.possibleAnswers.map((answer, index) => ({
        ...answer,
        id: `${optionalQuizModel.id ?? ""}_${index}`,
      })),
    };
    this.questionDimensions.push(optionalQuestionDimension);
    this.answerDimensions.push(...answersDimension);
  }

  private bindOnAnswerIds(resultsLink: string, resultsLinkWithDevice: string) {
    const questionTemplates: Record<string, FlavorFinderQuestions> = Object.fromEntries(
      this.quizTransitions
        .filter((transition) => transition.question?.id && !transition.onAnswerId && !transition.results)
        .map((transition) => [transition.question!.id, transition])
    );

    this.quizModel?.forEach((question) => {
      question.data.answers.forEach((answer, index) => {
        // Source question and answer ID
        const transitionId = `${question.id}_${index}`;

        // ID of the target question (the question this answer leads to)
        const targetQuestionId = answer.relation?.id;

        if (targetQuestionId) {
          const template = questionTemplates[targetQuestionId];

          if (template) {
            // If the question doesn't have onAnswerId yet, assign it to the original
            if (!template.onAnswerId) {
              template.onAnswerId = transitionId;
            } else {
              // Otherwise, create a copy with the new onAnswerId
              this.quizTransitions.push({
                ...template,
                onAnswerId: transitionId,
              });
            }
          }
        }

        // For each answer that leads to a result (resultRelation)
        const resultRelationId = answer.resultRelation?.id;
        if (resultRelationId) {
          const resultItem = this.resultsModel?.find((item) => item.id === resultRelationId);

          if (resultItem) {
            const results = resultItem.data.results
              .map((result) => {
                const resultModel = this.resultModel?.find((item) => result.data.id === item.id)?.data;

                return (
                  resultModel && {
                    ...resultModel,
                    bgGradientAngle: "135deg",
                    bgGradientColorFrom: resultModel.backgroundColor,
                    bgGradientColorTo: this.darkenHexColor(resultModel.backgroundColor, 20),
                    imageUrl: resultModel.exposedImageDesktop,
                  }
                );
              })
              .filter(Boolean);

            // Add a separate results transition for each answer
            this.quizTransitions.push({
              onAnswerId: transitionId,
              results: results,
              resultsLinkWithDevice: this.dynamicallyAssociate(
                resultsLinkWithDevice,
                results as FlavorFinderQuizResults[]
              ),
              resultsLink: this.dynamicallyAssociate(resultsLink, results as FlavorFinderQuizResults[]),
              headerUrl: results[0]?.splashImageDesktop,
            } as FlavorFinderQuestions);
          }
        }
      });
    });
  }

  private getAnswerIdForResultRelation = (
    quizModel: QuizModelType[] | undefined,
    resultRelationId: string
  ): string | undefined => {
    for (const question of quizModel ?? []) {
      const answerIndex = question.data.answers.findIndex((answer) => answer.resultRelation?.id === resultRelationId);
      if (answerIndex !== -1) {
        return `${question.id}_${answerIndex}`;
      }
    }
    return undefined;
  };
}
